import * as React from 'react'
import { IconName } from '../../../../common/src/components/Icon'
import { ColorName } from '../../../../common/src/constants'
import { TestIds } from '../../../../common/src/data/testids'
import { nullToEmpty, formatShortUrl, formatPasswordDots } from '../../../../common/src/format'
import memoize from '../../../../common/src/memoize'
import validate, { Validator } from '../../../../common/src/validate'
import EditBox, { CascadeState, Field, InfoBoxPellet } from './EditBox'
import EditBoxLarge from './EditBoxLarge'

export default class EditBoxes extends React.PureComponent<
  {
    model: { [key: string]: any }
    cascadeParent?: { [key: string]: any } | null
    cascadeChild?: { [key: string]: any } | null
    fields: Field<any>[]
    displayType: 'small' | 'large' | 'code'
    onEdit?: (key: string, value: any) => Promise<any>
    onDelete?: (() => void) | null
    disableEdit?: boolean // if true, disable editing all boxes
    color?: ColorName
  },
  {
    editKey: string | null
    error?: string
  }
> {
  private focusHandlers = {}
  private blurHandlers = {}
  private editHandlers = {}

  constructor(props: EditBoxes['props']) {
    super(props)
    this.state = {
      editKey: null,
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onWindowKeyDownBound)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onWindowKeyDownBound)
  }

  render() {
    const { props, state } = this
    const { model, displayType, cascadeParent, cascadeChild, disableEdit, color } = props
    const { editKey, error } = state

    return (
      <span data-testid={TestIds.editBoxes}>
        {props.fields.map(field => {
          const valModel = model[field.key] == null ? null : model[field.key]
          const valChild = cascadeChild ? cascadeChild[field.key] : null
          const valParent = cascadeParent ? cascadeParent[field.key] : null
          let cascadeState: CascadeState
          let val: any
          let sublabel: string | undefined
          let labelColor: ColorName | undefined
          if (valChild != null) {
            // false means the value has been set; do cascade
            cascadeState = 'OVERRIDDEN_BY_CHILD'
            val = valModel // still set value to model value, so edit brings up this not cascaded value
            sublabel = field.overridenByChildLabel || undefined
            labelColor = 'silver'
          } else if (valModel != null) {
            // false means the value has been set; don't cascade
            cascadeState = 'OWN_VALUE'
            val = valModel
            sublabel = undefined
            labelColor = undefined
          } else if (valParent != null) {
            cascadeState = 'DEFAULT_TO_PARENT'
            val = valParent // valModel is undefined; set value to parent model
            sublabel = field.defaultToParentLabel == null ? undefined : field.defaultToParentLabel(val)
            labelColor = 'silver'
          } else {
            cascadeState = 'MISSING'
            sublabel = 'missing'
            labelColor = 'midway'
          }

          labelColor = color || labelColor

          return this.renderField(
            field,
            val,
            cascadeState,
            displayType,
            disableEdit,
            editKey || undefined,
            error,
            labelColor,
            sublabel
          )
        })}
        {props.onDelete && (
          <InfoBoxPellet key="delete" hoverColor="coral" isEditable onClick={props.onDelete} color={color}>
            &times;
          </InfoBoxPellet>
        )}
      </span>
    )
  }

  private renderField(
    field: Field<any>,
    val: any,
    cascadeState: CascadeState,
    displayType: string,
    disableEdit?: boolean,
    editKey?: string,
    error?: string,
    labelColor?: ColorName,
    sublabel?: string
  ) {
    let icon: IconName | undefined
    if (field.iconFunc) {
      icon = field.iconFunc(val)
    }
    const canEdit = disableEdit ? false : field.isEditable || false
    if (displayType === 'large' || displayType === 'code') {
      return (
        <EditBoxLarge
          key={field.key}
          canEdit={canEdit}
          canDelete={false}
          onKeyDown={this.onKeyDownBound}
          onFocus={memoize(this.focusHandlers, field.key, (a: string) => () => this.onFocus(a))}
          onBlur={memoize(this.blurHandlers, field.key, () => () => this.onBlurBound())}
          isEditing={editKey === field.key}
          error={editKey === field.key ? error : undefined}
          cascadeState={cascadeState}
          labelColor={labelColor}
          label={field.label || field.key}
          placeholder={field.placeholder}
          sublabel={sublabel || null}
          body={val || ''}
          monospace={displayType === 'code'}
          submitEdit={memoize(
            this.editHandlers,
            field.key,
            (key: string) => (value: string) => this.submitEdit(value, key, field.validator)
          )}
        />
      )
    }
    return (
      <EditBox
        key={field.key}
        val={val}
        cascadeState={cascadeState}
        field={field}
        icon={icon}
        isEditing={editKey === field.key}
        hasError={editKey === field.key ? Boolean(error) : false}
        onFocus={memoize(this.focusHandlers, field.key, (a: string) => () => this.onFocus(a))}
        onKeyDown={this.onKeyDownBound}
        onBlur={this.onBlurBound}
        disableEdit={!canEdit}
        color={labelColor}
      />
    )
  }

  private onFocus(key: string) {
    const { editKey } = this.state
    if (editKey === key) {
      return
    }
    const field = this.props.fields.find(f => f.key === key)
    if (field == null) {
      return
    }
    if (!field.isEditable) {
      return
    }
    this.setState({ editKey: key, error: undefined })
  }

  private onBlurBound = () => {
    this.stopEditing()
  }

  private onKeyDownBound = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { editKey } = this.state
    if (editKey == null) {
      return
    }
    const field = this.props.fields.find(f => f.key === editKey)
    if (field == null) {
      return
    }
    const { isEditable, options, isOptional } = field
    if (!isEditable) {
      return
    }

    let validator
    if (options != null) {
      validator = validate
        .str()
        .valid(options as string[])
        .caseInsensitive()
      if (isOptional) {
        validator = validator.empty('')
      }
    } else {
      validator = field.validator
    }

    if (e.keyCode === 13 || e.keyCode === 9) {
      // tab or enter
      const str = e.currentTarget.value
      this.submitEdit(str, editKey, validator)
    } else if (e.key === 'Escape') {
      this.stopEditing()
    }
    e.stopPropagation()
  }

  private onWindowKeyDownBound = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.state.editKey != null) {
      this.stopEditing()
    }
  }

  private submitEdit(value: string, editKey: string, validator?: Validator<any> | null) {
    const val = validator == null ? { value } : validator.validate(value)
    if (val.error == null) {
      if (!this.props.onEdit) {
        console.warn('isEditable is set, onEdit is missing: ' + editKey)
      } else {
        const cleanVal = val.value == null ? null : val.value
        this.props
          .onEdit(editKey, cleanVal)
          .then(() => this.setState({ error: undefined, editKey: null }))
          .catch(e => {
            this.setState({ error: e.message, editKey })
          })
      }
    } else {
      this.setState({ error: val.error.message })
    }
  }

  private stopEditing() {
    this.setState({ error: undefined, editKey: null })
  }

  static optUser(key: string, missingPrompt: string, placeholder?: string): Field<string> {
    return {
      key,
      isEditable: true,
      textFunc: val => val || missingPrompt,
      editTextFunc: nullToEmpty,
      validator: validate.username().empty(''),
      placeholder,
    }
  }

  static optEmail(key: string, missingPrompt: string, placeholder?: string): Field<string> {
    return {
      key,
      isEditable: true,
      textFunc: val => val || missingPrompt,
      editTextFunc: nullToEmpty,
      validator: validate.email().empty(''),
      placeholder,
    }
  }

  static optUrl(key: string, missingPrompt: string, prefix: string, placeholder?: string): Field<string> {
    return {
      key,
      isEditable: true,
      textFunc: val => (val ? prefix + formatShortUrl(val) : missingPrompt),
      editTextFunc: nullToEmpty,
      validator: validate.url().empty(''),
      placeholder,
    }
  }

  static optPassword(key: string, missingPrompt: string, placeholder?: string): Field<string> {
    return {
      key,
      isEditable: true,
      textFunc: val => (val ? formatPasswordDots(val) : missingPrompt),
      editTextFunc: nullToEmpty,
      validator: validate.str().empty(''),
      placeholder,
    }
  }
}
